import { Component } from '@theme/component';

const MOBILE_MEDIA_QUERY = '(max-width: 989px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Controls the active desktop or mobile video in the split hero.
 *
 * @typedef {object} HeroSplitRefs
 * @property {HTMLVideoElement} [desktopVideo] - Desktop Shopify-hosted video.
 * @property {HTMLVideoElement} [mobileVideo] - Optional mobile Shopify-hosted video.
 * @property {HTMLElement} [controls] - Video controls container.
 * @property {HTMLButtonElement} [playButton] - Play/pause button.
 * @property {HTMLButtonElement} [muteButton] - Mute/unmute button.
 * @property {HTMLElement} [playIcon] - Play icon.
 * @property {HTMLElement} [pauseIcon] - Pause icon.
 * @property {HTMLElement} [mutedIcon] - Muted icon.
 * @property {HTMLElement} [unmutedIcon] - Unmuted icon.
 * @property {HTMLElement} [primaryQuizTrigger] - Optional primary CTA that opens the header quiz.
 * @property {HTMLElement} [secondaryQuizTrigger] - Optional secondary CTA that opens the header quiz.
 *
 * @extends {Component<HeroSplitRefs>}
 */
class HeroSplitComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  #mobileMedia = window.matchMedia(MOBILE_MEDIA_QUERY);
  #reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;

    for (const video of this.#videos) {
      video.pause();
    }
  }

  async togglePlayback() {
    const video = this.#activeVideo;
    if (!video) return;

    if (video.paused || video.ended) {
      try {
        await video.play();
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
          console.warn('Unable to play split hero video.', error);
        }
      }
    } else {
      video.pause();
    }

    this.#updateControls();
  }

  toggleMute() {
    const video = this.#activeVideo;
    if (!video) return;

    video.muted = !video.muted;
    this.#updateControls();
  }

  /**
   * Opens the quiz dialog configured by the header section.
   * @param {MouseEvent} event
   */
  openQuiz(event) {
    const headerTrigger = this.#headerQuizTrigger;
    if (!headerTrigger) return;

    event.preventDefault();
    this.#syncQuizTriggers(headerTrigger);
    headerTrigger.click();
  }

  #setup() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#mobileMedia.addEventListener('change', this.#activateMedia, { signal });
    this.#reducedMotion.addEventListener('change', this.#activateMedia, { signal });

    for (const video of this.#videos) {
      for (const eventName of ['play', 'pause', 'ended', 'volumechange', 'loadeddata']) {
        video.addEventListener(eventName, this.#updateControls, { signal });
      }
    }

    this.#syncQuizTriggers();
    this.#activateMedia();
  }

  #activateMedia = () => {
    const activeVideo = this.#activeVideo;

    for (const video of this.#videos) {
      if (video !== activeVideo) video.pause();
    }

    if (!activeVideo) {
      this.#updateControls();
      return;
    }

    if (this.#reducedMotion.matches || this.dataset.autoplay !== 'true') {
      activeVideo.pause();
      this.#updateControls();
      return;
    }

    activeVideo.muted = true;
    activeVideo.play().catch(() => {
      this.#updateControls();
    });
    this.#updateControls();
  };

  #updateControls = () => {
    const video = this.#activeVideo;
    const { controls, playButton, muteButton, playIcon, pauseIcon, mutedIcon, unmutedIcon } = this.refs;

    if (!(controls instanceof HTMLElement)) return;

    controls.hidden = !video;
    if (!video) return;

    const isPlaying = !video.paused && !video.ended;
    const isMuted = video.muted;

    if (playIcon instanceof HTMLElement) playIcon.hidden = isPlaying;
    if (pauseIcon instanceof HTMLElement) pauseIcon.hidden = !isPlaying;
    if (mutedIcon instanceof HTMLElement) mutedIcon.hidden = !isMuted;
    if (unmutedIcon instanceof HTMLElement) unmutedIcon.hidden = isMuted;

    if (playButton instanceof HTMLButtonElement) {
      playButton.setAttribute('aria-pressed', String(isPlaying));
      playButton.setAttribute('aria-label', isPlaying ? this.#label('pause') : this.#label('play'));
    }

    if (muteButton instanceof HTMLButtonElement) {
      muteButton.setAttribute('aria-pressed', String(isMuted));
      muteButton.setAttribute('aria-label', isMuted ? this.#label('unmute') : this.#label('mute'));
    }
  };

  /**
   * Copies the existing header trigger's dialog relationship onto enabled hero CTAs.
   * @param {HTMLButtonElement | null} [headerTrigger]
   */
  #syncQuizTriggers(headerTrigger = this.#headerQuizTrigger) {
    const controls = headerTrigger?.getAttribute('aria-controls');

    for (const heroTrigger of [this.refs.primaryQuizTrigger, this.refs.secondaryQuizTrigger]) {
      if (!(heroTrigger instanceof HTMLElement)) continue;

      if (controls) {
        heroTrigger.setAttribute('aria-controls', controls);
      } else {
        heroTrigger.removeAttribute('aria-controls');
      }
    }
  }

  /** @returns {HTMLButtonElement | null} */
  get #headerQuizTrigger() {
    const trigger = document.querySelector(
      'button.quiz-modal-trigger:not(.quiz-modal-trigger--drawer)[aria-controls]'
    );
    return trigger instanceof HTMLButtonElement ? trigger : null;
  }

  /** @returns {HTMLVideoElement[]} */
  get #videos() {
    return [this.refs.desktopVideo, this.refs.mobileVideo].filter(
      (video) => video instanceof HTMLVideoElement
    );
  }

  /** @returns {HTMLVideoElement | undefined} */
  get #activeVideo() {
    if (this.#mobileMedia.matches && this.dataset.hasCustomMobileMedia === 'true') {
      return this.refs.mobileVideo instanceof HTMLVideoElement ? this.refs.mobileVideo : undefined;
    }

    return this.refs.desktopVideo instanceof HTMLVideoElement ? this.refs.desktopVideo : undefined;
  }

  /**
   * @param {'play' | 'pause' | 'mute' | 'unmute'} action
   * @returns {string}
   */
  #label(action) {
    const label = this.dataset[`label${action.charAt(0).toUpperCase()}${action.slice(1)}`];
    return label || `${action} video`;
  }
}

if (!customElements.get('hero-split-component')) {
  customElements.define('hero-split-component', HeroSplitComponent);
}
